import {
  ApiException,
  completeFile,
  completeTransfer,
  initiateTransfer,
  uploadPart,
  type InitiateResponse,
} from './api';
import { DEFAULT_RETENTION, MAX_FILE_SIZE, RETENTION_OPTIONS, SHARE_BASE } from './config';
import { copyText, escapeHtml, icon, showToast } from './dom';
import { daysLeft, formatBytes, formatDateTime } from './format';

interface UploadState {
  files: File[];
  retentionDays: number;
  passwordEnabled: boolean;
  passwordMode: PasswordMode;
  emailEnabled: boolean;
  emailShareUrl: boolean;
  emailRequireAuth: boolean;
  emailSharePassword: boolean;
}

type PasswordMode = 'manual' | 'auto';

const GENERATED_PASSWORD_LENGTH = 16;
const PASSWORD_GROUPS = [
  'ABCDEFGHJKLMNPQRSTUVWXYZ',
  'abcdefghijkmnpqrstuvwxyz',
  '23456789',
] as const;
const PASSWORD_CHARS = PASSWORD_GROUPS.join('');

// Recipient / package-context limits. These mirror the Worker's authoritative
// LIMITS (MAX_ALLOWED_EMAILS, MAX_MAIL_SENDER_LENGTH, MAX_MAIL_ITEM_LENGTH) so the
// client can warn before submitting; the Worker re-validates on every request.
const MAX_ALLOWED_EMAILS = 3;
const MAX_MAIL_SENDER_LENGTH = 100;
const MAX_MAIL_ITEM_LENGTH = 200;

// Lower-page explanatory content. Kept below the upload panel so the app stays
// the first screen. The FAQ copy below is rendered verbatim into both the
// visible accordion and the FAQPage structured data, so the two never drift.
interface SeoFeature {
  icon: string;
  title: string;
  body: string;
}

interface FaqItem {
  q: string;
  a: string;
}

const SEO_FEATURES: SeoFeature[] = [
  {
    icon: 'shield',
    title: '推測されにくい共有URL',
    body: '発行される受け取りURLは連番を含まないランダムな文字列。リンクを知っている人だけがファイルを受け取れます。',
  },
  {
    icon: 'key',
    title: 'パスワード付きで共有',
    body: 'URLとは別に合言葉を設定すれば、パスワードを知っている相手だけがダウンロードできます。自動生成にも対応。',
  },
  {
    icon: 'mail',
    title: 'メール送信とメール認証',
    body: '共有URLやパスワードを指定のアドレスへ自動送信。受け取り時に6桁コードのメール認証を求めることもできます。',
  },
  {
    icon: 'clock',
    title: '最大7日で自動削除',
    body: '保存期間は1〜7日から選択。期限を過ぎるとアップロードしたファイルは自動で完全に削除され、URLも無効になります。',
  },
];

const FAQ_ITEMS: FaqItem[] = [
  {
    q: '大容量ファイルはどのくらいのサイズまで共有できますか？',
    a: '1ファイルあたり5GBまでを目安に共有できます。写真や動画、設計データなど、メールに添付しにくい大きなファイルの受け渡しに向いています。',
  },
  {
    q: 'ファイルの保存期間は最大で何日ですか？',
    a: '保存期間は1日・3日・5日・7日から選べます。最大7日で、期限を過ぎるとアップロードしたファイルは自動的に完全に削除され、共有URLも無効になります。',
  },
  {
    q: 'パスワード付きでファイルを共有できますか？',
    a: 'はい。共有URLとは別に合言葉（パスワード）を設定できます。パスワードを知っている相手だけがダウンロードでき、そのパスワードをメールで自動送信することもできます。',
  },
  {
    q: 'メールでの共有やメール認証はできますか？',
    a: '指定したメールアドレス宛に共有URLを自動で送信できます。さらにメール認証を有効にすると、受け取る人はメールで届く6桁の認証コードを入力してからダウンロードします。',
  },
  {
    q: '登録やアプリのインストールは必要ですか？',
    a: '会員登録やインストールは不要です。ブラウザでファイルを選び、共有URLを発行して相手に伝えるだけで受け渡しが完了します。',
  },
];

const state: UploadState = {
  files: [],
  retentionDays: DEFAULT_RETENTION,
  passwordEnabled: false,
  passwordMode: 'auto',
  emailEnabled: false,
  emailShareUrl: false,
  emailRequireAuth: false,
  emailSharePassword: false,
};

export function renderUpload(root: HTMLElement): void {
  // Reset state on (re)mount.
  state.files = [];
  state.retentionDays = DEFAULT_RETENTION;
  state.passwordEnabled = false;
  state.passwordMode = 'auto';
  state.emailEnabled = false;
  state.emailShareUrl = false;
  state.emailRequireAuth = false;
  state.emailSharePassword = false;
  root.innerHTML = introHtml() + `<div class="panel" id="panel">${formHtml()}</div>` + seoHtml();
  wireForm(root);
}

function seoHtml(): string {
  const features = SEO_FEATURES.map(
    (f) => `
      <div class="seo-card">
        <span class="seo-ic">${icon(f.icon)}</span>
        <div>
          <h3>${f.title}</h3>
          <p>${f.body}</p>
        </div>
      </div>`,
  ).join('');

  const faqs = FAQ_ITEMS.map(
    (f) => `
      <details class="faq-item">
        <summary class="faq-q">${f.q}</summary>
        <div class="faq-a">${f.a}</div>
      </details>`,
  ).join('');

  return `
  <section class="wrap seo" aria-labelledby="seo-title">
    <div class="seo-head">
      <p class="seo-eyebrow">一時ファイル共有について</p>
      <h2 id="seo-title">大容量ファイルを、期限付きで手軽に共有</h2>
      <p>小包み.ビズは、写真・動画・資料などの大容量ファイルを一時的に共有できるファイル転送サービスです。登録なしで推測されにくい専用URLを発行し、必要に応じてパスワード付きの共有やメール認証を追加できます。保存期間は最大7日で、期限を過ぎたファイルは自動で完全に削除されます。</p>
    </div>
    <div class="seo-grid">${features}</div>
    <div class="seo-faq">
      <h2>よくある質問</h2>
      <div class="faq-list">${faqs}</div>
    </div>
    ${faqJsonLd()}
  </section>`;
}

// Structured data for the visible FAQ above. Built from the same FAQ_ITEMS so
// the markup always matches what the user can read on the page.
function faqJsonLd(): string {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
  // Escape `<` so the serialized JSON can never terminate the <script> element.
  const json = JSON.stringify(data).replace(/</g, '\\u003c');
  return `<script type="application/ld+json">${json}</script>`;
}

function introHtml(): string {
  return `
  <div class="wrap intro">
    <h1>ファイルを一時的に共有</h1>
    <p>推測困難なURLを発行して共有します。メールでのURL共有、パスワード共有、メール認証は必要な分だけ追加できます。実ファイルはアップロード先の R2 に保存され、保存期間の経過後に自動で完全に削除されます。</p>
    <div class="spec">
      <span class="chip">${icon('up')}1ファイル5GBまで推奨</span>
      <span class="chip">${icon('clock')}最大7日間保存</span>
      <span class="chip">${icon('shield')}URLは推測困難</span>
      <span class="chip">${icon('lock')}メール共有 / 認証は任意</span>
    </div>
  </div>`;
}

function formHtml(): string {
  const segButtons = RETENTION_OPTIONS.map(
    (d) =>
      `<button type="button" data-retention="${d}" aria-pressed="${d === state.retentionDays}">${d}日</button>`,
  ).join('');
  return `
  <div class="screen">
    <div class="up-grid">
      <div>
        <label class="drop" data-drop>
          <input type="file" class="sr-file" id="file-input" multiple aria-label="共有するファイルを選択" />
          <span class="drop-ic">${icon('up')}</span>
          <span class="drop-title"><span>ファイルをドラッグ、または</span><span class="nowrap">クリックして選択</span></span>
          <span class="hint">1ファイル5GBまで推奨 / 最大7日間保存</span>
        </label>
        <div id="selected" class="files" style="margin-top:14px"></div>
        <div class="opts">
          <div class="field">
            <span class="lb">保存期間</span>
            <div class="seg" role="group" aria-label="保存期間" id="retention">${segButtons}</div>
          </div>
          <div class="switch-row">
            <div><p class="st">${icon('key')}パスワード保護</p><p class="sd">URLとは別の合言葉を要求します</p></div>
            <label class="switch"><input type="checkbox" id="pw-toggle" aria-label="パスワード保護を有効化" /><span class="track"></span></label>
          </div>
          <div class="field" id="pw-field" hidden>
            <div class="field-head">
              <label for="pw-input">パスワード（4文字以上）</label>
              <div class="seg seg-sm" role="group" aria-label="パスワードの入力方法" id="pw-mode">
                <button type="button" data-password-mode="manual" aria-pressed="false">手入力</button>
                <button type="button" data-password-mode="auto" aria-pressed="true">自動生成</button>
              </div>
            </div>
            <div class="input-action">
              <input class="control" id="pw-input" type="text" autocomplete="off" placeholder="自動生成されたパスワード" readonly />
              <button class="btn btn-ghost sm icon-btn" type="button" id="pw-copy" aria-label="パスワードをコピー" disabled>${icon('copy')}</button>
              <button class="btn btn-ghost sm" type="button" id="pw-generate">${icon('refresh')}再生成</button>
            </div>
            <p class="hint">自動生成では I / l / 1 / O / 0 / o を使いません。</p>
          </div>
          <div class="switch-row">
            <div><p class="st">${icon('mail')}メールアドレスを指定</p><p class="sd" id="mail-summary">メールアドレスを自動共有や認証に使う場合はONにしてください</p></div>
            <label class="switch"><input type="checkbox" id="mail-toggle" aria-label="メールアドレス指定を有効化" /><span class="track"></span></label>
          </div>
          <div class="field" id="mail-field" hidden>
            <div class="mail-purpose-row" id="mail-purpose-row" role="group" aria-label="メールアドレスの使い方">
              <span class="lb">メールアドレスの使い方</span>
              <label class="purpose">
                <input type="checkbox" id="mail-share-url" />
                <span><span class="pt">共有URLを自動で送る</span><span class="pd">発行後、指定した相手に受け取り用URLをメールします。</span></span>
              </label>
              <label class="purpose">
                <input type="checkbox" id="mail-require-auth" />
                <span><span class="pt">受け取り時にメール認証する</span><span class="pd">相手はメールで届く6桁コードを入力してから受け取ります。</span></span>
              </label>
              <label class="purpose" id="mail-share-password-row">
                <input type="checkbox" id="mail-share-password" />
                <span><span class="pt">パスワードを自動で送る</span><span class="pd">パスワード保護がONのとき、合言葉を指定した相手へメールします。</span></span>
              </label>
              <p class="hint" id="mail-purpose-hint"></p>
            </div>
            <label for="mail-input" id="mail-label">送信先または認証に使うメールアドレス（改行・カンマ区切り・最大${MAX_ALLOWED_EMAILS}件）</label>
            <textarea class="control" id="mail-input" placeholder="client@example.com"></textarea>
            <p class="hint" id="mail-input-hint"></p>
            <div class="mail-context" id="mail-context">
              <span class="lb">受け取る人に伝える内容</span>
              <div class="mail-context-field">
                <label for="mail-sender">差出人（送り主）</label>
                <input class="control" id="mail-sender" type="text" maxlength="${MAX_MAIL_SENDER_LENGTH}" autocomplete="off" placeholder="例：小包 太郎／株式会社こづつみ" />
              </div>
              <div class="mail-context-field">
                <label for="mail-item">包みの品目（お届けするもの）</label>
                <input class="control" id="mail-item" type="text" maxlength="${MAX_MAIL_ITEM_LENGTH}" autocomplete="off" placeholder="例：お見積書と契約書一式" />
              </div>
              <p class="hint">差出人と品目は送信するメールに記載され、受け取る人が「誰からの何か」を確認できます。</p>
            </div>
          </div>
        </div>
        <div class="screen-foot">
          <p class="hint">発行されるURLは推測困難な文字列です</p>
          <button class="btn btn-accent" type="button" id="submit" disabled>${icon('link')}共有URLを発行</button>
        </div>
      </div>
      <aside class="notes">
        <h3>保存ポリシー</h3>
        <ul>
          <li>${icon('shield')}R2に保存し、URLは連番を含みません</li>
          <li>${icon('clock')}保存期間の経過後に自動で完全に削除します</li>
          <li>${icon('lock')}メールはURL共有、認証、パスワード共有を必要な分だけ選べます</li>
        </ul>
      </aside>
    </div>
  </div>`;
}

function wireForm(root: HTMLElement): void {
  const fileInput = root.querySelector<HTMLInputElement>('#file-input')!;
  const drop = root.querySelector<HTMLLabelElement>('[data-drop]')!;
  const submit = root.querySelector<HTMLButtonElement>('#submit')!;

  fileInput.addEventListener('change', () => {
    if (fileInput.files) addFiles(root, Array.from(fileInput.files));
    fileInput.value = '';
  });

  ['dragenter', 'dragover'].forEach((ev) =>
    drop.addEventListener(ev, (e) => {
      e.preventDefault();
      drop.classList.add('drag');
    }),
  );
  ['dragleave', 'drop'].forEach((ev) =>
    drop.addEventListener(ev, (e) => {
      e.preventDefault();
      drop.classList.remove('drag');
    }),
  );
  drop.addEventListener('drop', (e) => {
    const dt = (e as DragEvent).dataTransfer;
    if (dt?.files) addFiles(root, Array.from(dt.files));
  });

  root.querySelector('#retention')!.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-retention]');
    if (!btn) return;
    state.retentionDays = Number(btn.dataset.retention);
    root
      .querySelectorAll<HTMLButtonElement>('#retention button')
      .forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
  });

  wireToggle(root, '#pw-toggle', '#pw-field', (on) => {
    state.passwordEnabled = on;
    if (on && state.passwordMode === 'auto') fillGeneratedPassword(root, false);
    if (on && state.emailEnabled) state.emailSharePassword = true;
    if (!on) state.emailSharePassword = false;
    syncMailUi(root);
  });
  wireToggle(root, '#mail-toggle', '#mail-field', (on) => {
    state.emailEnabled = on;
    if (on && !state.emailShareUrl && !state.emailRequireAuth && !state.emailSharePassword) {
      state.emailShareUrl = true;
      state.emailSharePassword = state.passwordEnabled;
    }
    syncMailUi(root);
  });
  wirePasswordMode(root);
  wireMailPurposes(root);
  syncMailUi(root);

  submit.addEventListener('click', () => {
    void startUpload(root);
  });
}

function wirePasswordMode(root: HTMLElement): void {
  root.querySelector('#pw-mode')?.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-password-mode]');
    if (!btn) return;
    const mode = btn.dataset.passwordMode === 'auto' ? 'auto' : 'manual';
    setPasswordMode(root, mode);
  });

  root.querySelector('#pw-generate')?.addEventListener('click', () => fillGeneratedPassword(root, true));
  root.querySelector('#pw-copy')?.addEventListener('click', () => void copyPassword(root));

  const input = root.querySelector<HTMLInputElement>('#pw-input');
  const copy = root.querySelector<HTMLButtonElement>('#pw-copy');
  if (input && copy) {
    const syncCopy = () => {
      copy.disabled = input.value.length === 0;
    };
    input.addEventListener('input', syncCopy);
    syncCopy();
  }

  syncPasswordModeUi(root, state.passwordMode);
}

async function copyPassword(root: HTMLElement): Promise<void> {
  const input = root.querySelector<HTMLInputElement>('#pw-input');
  const password = input?.value ?? '';
  if (!password.trim()) {
    showToast('コピーするパスワードがありません', true);
    return;
  }
  await copyText(password);
}

function setPasswordMode(root: HTMLElement, mode: PasswordMode): void {
  state.passwordMode = mode;
  syncPasswordModeUi(root, mode);

  if (mode === 'auto') fillGeneratedPassword(root, true);
}

function syncPasswordModeUi(root: HTMLElement, mode: PasswordMode): void {
  root
    .querySelectorAll<HTMLButtonElement>('#pw-mode button[data-password-mode]')
    .forEach((btn) => btn.setAttribute('aria-pressed', String(btn.dataset.passwordMode === mode)));

  const input = root.querySelector<HTMLInputElement>('#pw-input');
  const generate = root.querySelector<HTMLButtonElement>('#pw-generate');
  if (!input || !generate) return;

  input.readOnly = mode === 'auto';
  input.placeholder = mode === 'auto' ? '自動生成されたパスワード' : '合言葉を入力';
  generate.hidden = mode !== 'auto';
}

function fillGeneratedPassword(root: HTMLElement, notify: boolean): void {
  const input = root.querySelector<HTMLInputElement>('#pw-input');
  if (!input) return;
  try {
    input.value = generateReadablePassword(GENERATED_PASSWORD_LENGTH);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    if (notify) showToast('パスワードを自動生成しました');
  } catch {
    showToast('このブラウザでは安全な自動生成が使えません', true);
  }
}

function wireMailPurposes(root: HTMLElement): void {
  const shareUrl = root.querySelector<HTMLInputElement>('#mail-share-url');
  const requireAuth = root.querySelector<HTMLInputElement>('#mail-require-auth');
  const sharePassword = root.querySelector<HTMLInputElement>('#mail-share-password');

  shareUrl?.addEventListener('change', () => {
    state.emailShareUrl = !!shareUrl.checked;
    syncMailUi(root);
  });
  requireAuth?.addEventListener('change', () => {
    state.emailRequireAuth = !!requireAuth.checked;
    syncMailUi(root);
  });
  sharePassword?.addEventListener('change', () => {
    state.emailSharePassword = !!sharePassword.checked && state.passwordEnabled;
    syncMailUi(root);
  });
}

function syncMailUi(root: HTMLElement): void {
  if (!state.passwordEnabled) state.emailSharePassword = false;

  const shareUrl = root.querySelector<HTMLInputElement>('#mail-share-url');
  const requireAuth = root.querySelector<HTMLInputElement>('#mail-require-auth');
  const sharePassword = root.querySelector<HTMLInputElement>('#mail-share-password');
  const sharePasswordRow = root.querySelector<HTMLElement>('#mail-share-password-row');
  const summary = root.querySelector<HTMLElement>('#mail-summary');
  const purposeHint = root.querySelector<HTMLElement>('#mail-purpose-hint');
  const inputHint = root.querySelector<HTMLElement>('#mail-input-hint');
  const label = root.querySelector<HTMLElement>('#mail-label');

  if (shareUrl) shareUrl.checked = state.emailShareUrl;
  if (requireAuth) requireAuth.checked = state.emailRequireAuth;
  if (sharePassword) {
    sharePassword.checked = state.emailSharePassword;
    sharePassword.disabled = !state.passwordEnabled;
  }
  if (sharePasswordRow) sharePasswordRow.classList.toggle('disabled', !state.passwordEnabled);

  if (!summary || !purposeHint || !inputHint || !label) return;

  if (!state.emailEnabled) {
    summary.textContent = 'メールアドレスを自動共有や認証に使う場合はONにしてください';
    return;
  }

  const selected: string[] = [];
  if (state.emailShareUrl) selected.push('共有URLを送ります');
  if (state.emailRequireAuth) selected.push('受け取り時にメール認証します');
  if (state.emailSharePassword) selected.push('パスワードを送ります');

  summary.textContent = selected.length > 0
    ? selected.join(' / ')
    : '使い方を1つ以上選んでください';
  purposeHint.textContent = state.passwordEnabled
    ? 'URL共有、メール認証、パスワード共有は必要なものだけ選べます。'
    : 'パスワードを送るには、先にパスワード保護をONにしてください。';
  label.textContent = `送信先または認証に使うメールアドレス（改行・カンマ区切り・最大${MAX_ALLOWED_EMAILS}件）`;
  inputHint.textContent = state.emailRequireAuth
    ? '認証を選ぶと、入力したメールアドレスだけが6桁コードを受け取れます。'
    : '自動共有を選ぶと、入力したメールアドレスへ発行後にメールします。';
}

function generateReadablePassword(length: number): string {
  if (length < PASSWORD_GROUPS.length) throw new Error('password length is too short');
  const chars = [
    ...PASSWORD_GROUPS.map((group) => group[randomIndex(group.length)]),
    ...Array.from({ length: length - PASSWORD_GROUPS.length }, () => PASSWORD_CHARS[randomIndex(PASSWORD_CHARS.length)]),
  ];
  shuffle(chars);
  return chars.join('');
}

function randomIndex(max: number): number {
  const cryptoApi = globalThis.crypto;
  if (!cryptoApi?.getRandomValues) throw new Error('crypto.getRandomValues is unavailable');

  const range = 0x100000000;
  const limit = range - (range % max);
  const value = new Uint32Array(1);
  do {
    cryptoApi.getRandomValues(value);
  } while (value[0] >= limit);
  return value[0] % max;
}

function shuffle(items: string[]): void {
  for (let i = items.length - 1; i > 0; i--) {
    const j = randomIndex(i + 1);
    [items[i], items[j]] = [items[j], items[i]];
  }
}

function wireToggle(root: HTMLElement, toggleSel: string, fieldSel: string, onChange: (on: boolean) => void): void {
  const toggle = root.querySelector<HTMLInputElement>(toggleSel)!;
  const field = root.querySelector<HTMLElement>(fieldSel)!;
  toggle.addEventListener('change', () => {
    field.hidden = !toggle.checked;
    onChange(toggle.checked);
  });
}

function addFiles(root: HTMLElement, incoming: File[]): void {
  for (const file of incoming) {
    const dup = state.files.some(
      (f) => f.name === file.name && f.size === file.size && f.lastModified === file.lastModified,
    );
    if (!dup) state.files.push(file);
  }
  renderSelected(root);
}

function removeFile(root: HTMLElement, index: number): void {
  state.files.splice(index, 1);
  renderSelected(root);
}

function renderSelected(root: HTMLElement): void {
  const container = root.querySelector<HTMLElement>('#selected')!;
  const submit = root.querySelector<HTMLButtonElement>('#submit')!;
  const oversize = state.files.filter((f) => f.size > MAX_FILE_SIZE);

  container.innerHTML = state.files
    .map(
      (f, i) => `
    <div class="file">
      <span class="fi">${icon('up')}</span>
      <div class="body">
        <p class="nm">${escapeHtml(f.name)}</p>
        <p class="mt">${formatBytes(f.size)}${f.size > MAX_FILE_SIZE ? ' / 上限超過' : ''}</p>
      </div>
      <button class="btn btn-ghost sm" type="button" data-remove="${i}" aria-label="削除">除外</button>
    </div>`,
    )
    .join('');

  container.querySelectorAll<HTMLButtonElement>('[data-remove]').forEach((btn) =>
    btn.addEventListener('click', () => removeFile(root, Number(btn.dataset.remove))),
  );

  submit.disabled = state.files.length === 0 || oversize.length > 0;
  if (oversize.length > 0) showToast('5GBを超えるファイルは共有できません', true);
}

async function startUpload(root: HTMLElement): Promise<void> {
  const panel = root.querySelector<HTMLElement>('#panel')!;
  const password = state.passwordEnabled
    ? (root.querySelector<HTMLInputElement>('#pw-input')?.value ?? '')
    : undefined;
  const emails = state.emailEnabled
    ? parseEmails(root.querySelector<HTMLTextAreaElement>('#mail-input')?.value ?? '')
    : undefined;
  const mailSender = state.emailEnabled
    ? (root.querySelector<HTMLInputElement>('#mail-sender')?.value ?? '').trim()
    : '';
  const mailItem = state.emailEnabled
    ? (root.querySelector<HTMLInputElement>('#mail-item')?.value ?? '').trim()
    : '';
  const notifyShareUrl = state.emailEnabled && state.emailShareUrl;
  const emailAuthRequired = state.emailEnabled && state.emailRequireAuth;
  const notifyPassword = state.emailEnabled && state.passwordEnabled && state.emailSharePassword;

  if (state.files.length === 0) return showToast('ファイルを選択してください', true);
  if (password !== undefined && password.length < 4) {
    return showToast('パスワードは4文字以上で入力してください', true);
  }
  if (state.emailEnabled && !notifyShareUrl && !emailAuthRequired && !notifyPassword) {
    return showToast('メールアドレスの使い方を1つ以上選んでください', true);
  }
  if (emails !== undefined && emails.length === 0) {
    return showToast('メールアドレスを入力してください', true);
  }
  if (emails !== undefined && emails.length > MAX_ALLOWED_EMAILS) {
    return showToast(`宛先メールは最大${MAX_ALLOWED_EMAILS}件までです`, true);
  }
  if (state.emailEnabled && !mailSender) {
    return showToast('差出人（送り主）を入力してください', true);
  }
  if (state.emailEnabled && !mailItem) {
    return showToast('包みの品目を入力してください', true);
  }

  const files = state.files.slice();
  panel.innerHTML = progressHtml(files);

  try {
    const init = await initiateTransfer({
      files: files.map((f) => ({ name: f.name, size: f.size, contentType: f.type || undefined })),
      retentionDays: state.retentionDays,
      password,
      allowedEmails: emails,
      emailAuthRequired,
      notifyShareUrl,
      notifyPassword,
      mailSender: state.emailEnabled ? mailSender : undefined,
      mailItem: state.emailEnabled ? mailItem : undefined,
    });

    for (let i = 0; i < files.length; i++) {
      const apiFile = init.files[i];
      await uploadOneFile(init, apiFile.fileId, files[i], (uploaded, total) =>
        updateProgress(panel, i, uploaded, total),
      );
      markFileDone(panel, i, files[i].size);
    }

    await completeTransfer(init.transferId, init.manageToken, { password, notifyShareUrl, notifyPassword });
    const shareUrl = `${SHARE_BASE}/d/${init.transferId}`;
    panel.innerHTML = doneHtml(shareUrl, init);
    wireDone(panel, shareUrl);
  } catch (err) {
    const message = err instanceof ApiException ? err.message : 'アップロードに失敗しました';
    panel.innerHTML = errorHtml(message);
    panel.querySelector('#retry')?.addEventListener('click', () => renderUpload(root));
  }
}

async function uploadOneFile(
  init: InitiateResponse,
  fileId: string,
  file: File,
  onProgress: (uploaded: number, total: number) => void,
): Promise<void> {
  const total = file.size;
  if (total === 0) {
    await withRetry(() => completeFile(init.transferId, fileId, init.manageToken));
    onProgress(0, 0);
    return;
  }
  let offset = 0;
  let partNumber = 1;
  let uploaded = 0;
  while (offset < total) {
    const end = Math.min(offset + init.chunkSize, total);
    const chunk = file.slice(offset, end);
    await withRetry(() => uploadPart(init.transferId, fileId, partNumber, init.manageToken, chunk));
    uploaded += end - offset;
    onProgress(uploaded, total);
    offset = end;
    partNumber += 1;
  }
  await withRetry(() => completeFile(init.transferId, fileId, init.manageToken));
}

async function withRetry<T>(fn: () => Promise<T>, attempts = 3): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      // Do not retry deterministic client errors (except throttling).
      if (err instanceof ApiException && err.status >= 400 && err.status < 500 && err.status !== 429) {
        throw err;
      }
      await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
    }
  }
  throw lastError;
}

function progressHtml(files: File[]): string {
  const rows = files
    .map(
      (f, i) => `
    <div class="file" id="row-${i}">
      <span class="fi" id="fi-${i}">${icon('up')}</span>
      <div class="body">
        <p class="nm">${escapeHtml(f.name)}</p>
        <p class="mt" id="mt-${i}">${formatBytes(f.size)} / multipart</p>
        <div class="bar"><i id="bar-${i}"></i></div>
      </div>
      <span class="pc" id="pc-${i}">0%</span>
    </div>`,
    )
    .join('');
  return `
  <div class="screen">
    <div class="files" aria-live="polite">${rows}</div>
    <p class="hint" style="margin-top:14px">アップロード完了後に共有URLを発行します。ページを閉じると中断されます。</p>
  </div>`;
}

function updateProgress(panel: HTMLElement, index: number, uploaded: number, total: number): void {
  const pct = total <= 0 ? 100 : Math.min(100, Math.round((uploaded / total) * 100));
  const bar = panel.querySelector<HTMLElement>(`#bar-${index}`);
  const pc = panel.querySelector<HTMLElement>(`#pc-${index}`);
  if (bar) bar.style.width = `${pct}%`;
  if (pc && !pc.classList.contains('done')) pc.textContent = `${pct}%`;
}

function markFileDone(panel: HTMLElement, index: number, size: number): void {
  const bar = panel.querySelector<HTMLElement>(`#bar-${index}`);
  const pc = panel.querySelector<HTMLElement>(`#pc-${index}`);
  const fi = panel.querySelector<HTMLElement>(`#fi-${index}`);
  const mt = panel.querySelector<HTMLElement>(`#mt-${index}`);
  if (bar) bar.style.width = '100%';
  if (pc) {
    pc.classList.add('done');
    pc.textContent = '完了';
  }
  if (fi) {
    fi.classList.add('done');
    fi.innerHTML = icon('check');
  }
  if (mt) mt.textContent = `${formatBytes(size)} / 完了`;
}

function doneHtml(shareUrl: string, init: InitiateResponse): string {
  const until = formatDateTime(init.expiresAt);
  const left = daysLeft(init.expiresAt);
  const badges = [
    `<span class="chip ok">${icon('check')}アップロード完了</span>`,
    `<span class="chip">${icon('clock')}残り${left}日（${escapeHtml(until)}まで）</span>`,
  ];
  if (init.requiresPassword) badges.push(`<span class="chip">${icon('key')}パスワード保護あり</span>`);
  if (init.notifiesShareUrl) badges.push(`<span class="chip">${icon('mail')}URL送信あり</span>`);
  if (init.notifiesPassword) badges.push(`<span class="chip">${icon('mail')}パスワード送信あり</span>`);
  if (init.requiresEmail) badges.push(`<span class="chip">${icon('mail')}メール認証あり</span>`);
  const notices: string[] = [];
  if (init.notifiesShareUrl) notices.push('共有URL');
  if (init.notifiesPassword) notices.push('パスワード');
  const completionHint = [
    notices.length > 0
      ? `指定したメールアドレスへ${notices.join('と')}を送りました。`
      : '受信者にこのURLを共有してください。',
    init.requiresEmail ? '受け取り時はメールの認証コードも必要です。' : '',
  ].filter(Boolean).join('');
  return `
  <div class="screen">
    <div class="done">
      <p class="eyebrow">発行済み</p>
      <h2 class="lead" id="share-ready" tabindex="-1">共有URLを発行しました</h2>
      <p class="muted"><span class="mono">${escapeHtml(until)}</span> まで有効です。</p>
      <div class="url-row">
        <code class="url" id="share-url" role="textbox" aria-readonly="true" aria-label="共有URL">${escapeHtml(shareUrl)}</code>
        <button class="btn btn-ink" type="button" id="copy-url">${icon('copy')}コピー</button>
      </div>
      <div class="badges">${badges.join('')}</div>
      <div class="screen-foot">
        <p class="hint">${completionHint}</p>
        <button class="btn btn-ghost" type="button" id="new-upload">${icon('refresh')}続けて送る</button>
      </div>
    </div>
  </div>`;
}

function wireDone(panel: HTMLElement, shareUrl: string): void {
  panel.querySelector<HTMLElement>('#share-ready')?.focus({ preventScroll: true });
  panel.scrollIntoView({ block: 'start' });
  panel.querySelector('#copy-url')?.addEventListener('click', () => void copyText(shareUrl));
  panel.querySelector('#new-upload')?.addEventListener('click', () => {
    const app = document.getElementById('app');
    if (app) renderUpload(app);
  });
}

function errorHtml(message: string): string {
  return `
  <div class="screen center">
    <span class="big-ic warn">${icon('alert')}</span>
    <p class="lead">アップロードに失敗しました</p>
    <p class="muted">${escapeHtml(message)}</p>
    <button class="btn btn-ghost" type="button" id="retry">${icon('refresh')}最初からやり直す</button>
  </div>`;
}

function parseEmails(raw: string): string[] {
  return Array.from(
    new Set(
      raw
        .split(/[\n,;]+/)
        .map((s) => s.trim().toLowerCase())
        .filter((s) => s.length > 0),
    ),
  );
}
